import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import TestimonialVideoCard from "@/components/TestimonialVideoCard";
import { usePublishedTestimonials, type PlayableTestimonial } from "@/hooks/useTestimonials";

/**
 * The responsive grid itself, with no data fetching, so the home page section
 * and the full page share one layout and cannot drift apart.
 *
 * The column count adapts to how many videos there actually are: one video
 * centred at reading width looks deliberate, whereas the same video stranded
 * in the left third of a three-column grid looks like two of them failed to
 * load.
 */
export const TestimonialGrid = ({ items }: { items: PlayableTestimonial[] }) => {
  if (!items.length) return null;

  const layout =
    items.length === 1
      ? "mx-auto max-w-2xl"
      : items.length === 2
        ? "mx-auto max-w-5xl sm:grid-cols-2"
        : "sm:grid-cols-2 lg:grid-cols-3";

  return (
    <div className={`grid grid-cols-1 gap-5 sm:gap-6 ${layout}`}>
      {items.map((t, i) => (
        <Reveal key={t.id} variant="up" delay={i * 90} className="h-full">
          <TestimonialVideoCard testimonial={t} priority={i === 0} />
        </Reveal>
      ))}
    </div>
  );
};

type Props = {
  /** Cap the home page at a tidy row; omit to show every video. */
  limit?: number;
  /** Link through to the full page when more videos exist than are shown. */
  showViewAll?: boolean;
  /**
   * Applied to the root. Spacing lives here rather than on a wrapper in the
   * caller: this component renders null when nothing is published, and a
   * wrapper would keep its own margin and leave a visible gap.
   */
  className?: string;
};

/**
 * Published testimonial videos, for the home page.
 *
 * Renders nothing at all until an admin has published at least one video: an
 * empty "Student Stories" heading over blank space is worse than no section,
 * and this feature ships before any content exists for it.
 */
const VideoTestimonials = ({ limit, showViewAll = true, className = "" }: Props) => {
  const { data, isLoading } = usePublishedTestimonials();

  if (isLoading || !data || data.length === 0) return null;

  const shown = limit ? data.slice(0, limit) : data;
  const hasMore = showViewAll && data.length > shown.length;

  return (
    <div className={className}>
      <TestimonialGrid items={shown} />
      {hasMore && (
        <div className="mt-8 text-center">
          <Link
            to="/testimonials"
            className="inline-flex items-center gap-1.5 rounded-lg border border-border/70 bg-white px-4 py-2.5 text-sm font-medium text-primary transition-all hover:border-secondary/40 hover:bg-secondary/5 active:scale-[0.97]"
          >
            Watch all {data.length} student stories
            <ArrowRight size={15} />
          </Link>
        </div>
      )}
    </div>
  );
};

export default VideoTestimonials;
